import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import { ApiError, AuthService, ConfigService, MessageService } from '../../../../core';
import { isLancamentoPeso, isLancamentoQuantidade, isLancamentoValor } from '../../../../models';
import { periodicidadeMensal } from '../../../../shared';
import { ConfigEmpresa } from '../../../configuracoes/app/empresa';
import { Cliente, TypeaheadClienteComponent } from '../../../pessoa/cliente';
import { RepresentanteCrudHttpService } from '../../../pessoa/representante';
import { calcularPesoProjetadoPerc, calcularQuantidadeProjetadaPerc, calcularValorProjetado, calcularValorProjetadoPerc } from '../../calculo/projetado';
import { calcularPesoRealizadoPerc, calcularQuantidadeRealizadaPerc, calcularValorRealizadoPerc } from '../../calculo/realizado';
import { ClienteAnaliseMercadoClassificacaoSearch } from '../../classificacao';
import { ClienteAnaliseMercadoClassificacaoLancamentoHttpService, ClienteAnaliseMercadoClassificacaoSearchLancamento, ClienteAnaliseMercadoClassificacaoSearchLancamentosFilter } from '../../classificacao/lancamento';
import { ClienteAnaliseMercadoLancamento, ClienteAnaliseMercadoLancamentoCreateModalComponent } from '../../lancamento';
import { perspectivaProjetada } from '../../perspectivas/cliente-analise-mercado-perspectiva';
import { ClienteAnaliseMercadoHttpService } from '../../services';
import { ClienteAnaliseMercadoStatusProjetadoService } from '../../status-projetado';
import { ClienteAnaliseMercadoProjecaoClassificacaoHttpService, ClienteAnaliseMercadoProjecaoClassificacaoSave } from '../classificacao';
import { ClienteAnaliseMercadoProjecaoSave } from '../cliente-analise-mercado-projecao-save';

@Component({
    selector: 'sgt-cliente-analise-mercado-projecao-form',
    templateUrl: './cliente-analise-mercado-projecao-form.component.html',
    styleUrls: ['./cliente-analise-mercado-projecao-form.component.less']
})
export class ClienteAnaliseMercadoProjecaoFormComponent implements OnInit {
    configEmpresa: ConfigEmpresa;
    isRepresentante: boolean;
    /** Se permite editar o cliente */
    canEditCliente = false;

    /** Sub-títulos do card das projeções */
    @Input() cardSubtitle: string = null;
    /** Se o component é embutido */
    @Input() embedded = false;
    /** ID do cliente das projeções */
    @Input() idCliente: number;
    /** Mês das projeções */
    @Input() mes = new Date().getMonth() + 1;
    /** Ano das projeções */
    @Input() ano = new Date().getFullYear();

    isLoading: boolean;
    entity: ClienteAnaliseMercadoClassificacaoSearch;

    @ViewChild('form') form: NgForm;
    @ViewChild('typeaeadCliente') typeaeadCliente: TypeaheadClienteComponent;
    @ViewChild('classificacaoCreateModal') classificacaoCreateModal: ClienteAnaliseMercadoLancamentoCreateModalComponent;

    constructor(private authService: AuthService,
                private configService: ConfigService,
                private messageService: MessageService,
                private representanteCrudHttpService: RepresentanteCrudHttpService,
                private clienteAnaliseMercadoHttpService: ClienteAnaliseMercadoHttpService,
                private clienteAnaliseMercadoClassificacaoLancamentoHttpService: ClienteAnaliseMercadoClassificacaoLancamentoHttpService,
                private clienteAnaliseMercadoStatusProjetadoService: ClienteAnaliseMercadoStatusProjetadoService,
                private clienteAnaliseMercadoProjecaoClassificacaoHttpService: ClienteAnaliseMercadoProjecaoClassificacaoHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.isRepresentante = this.authService.isRepresentante();

        if (this.canEditCliente && !this.idCliente) {
            this.insert();
        } else {
            this.edit(this.idCliente);
        }
    }

    /** Verifica se o lançamento da análise é por quantidade */
    isLancamentoQuantidade(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        return isLancamentoQuantidade(entityClassificacao?.lancamento);
    }

    /** Verifica se o lançamento da análise é por peso */
    isLancamentoPeso(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        return isLancamentoPeso(entityClassificacao?.lancamento);
    }

    /** Verifica se o lançamento da análise é por valor */
    isLancamentoValor(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        return isLancamentoValor(entityClassificacao?.lancamento);
    }

    /** Cria um novo objeto da análise de mercado para inserir as projeções para o cliente */
    private insert() {
        this.canEditCliente = true;

        this.entity = new ClienteAnaliseMercadoClassificacaoSearch();
        this.entity.mes = this.mes;
        this.entity.ano = this.ano;

        setTimeout(() => this.typeaeadCliente?.setFocus(), 200);
    }

    /** Carrega a análise de mercado do cliente para editar as suas projeções */
    private edit(idCliente: number) {
        this.canEditCliente = false;
        this.load(idCliente);
    }

    /** Carrega a análise de mercado do cliente com suas projeções */
    private load(idCliente: number) {
        this.isLoading = true;
        this.entity = null;

        // Carregando a análise de mercado do cliente no ano
        this.clienteAnaliseMercadoHttpService.load(idCliente, this.ano)
            .subscribe(
                analiseMercado => {
                    this.entity = new ClienteAnaliseMercadoClassificacaoSearch();
                    this.entity.id = analiseMercado.id;
                    this.entity.ano = analiseMercado.ano;
                    // Cliente
                    this.entity.idCliente = analiseMercado.idCliente;
                    this.entity.codigoIntegracaoErpCliente = analiseMercado.codigoIntegracaoErpCliente;
                    this.entity.cnpjCpfCliente = analiseMercado.cnpjCpfCliente;
                    this.entity.nomeCliente = analiseMercado.nomeCliente;
                    this.entity.nomeFantasiaCliente = analiseMercado.nomeFantasiaCliente;
                    this.entity.nomeCidadeCliente = analiseMercado.nomeCidadeCliente;
                    this.entity.ufCliente = analiseMercado.ufCliente;
                    // Representante
                    this.entity.idRepresentante = analiseMercado.idRepresentante;
                    this.entity.codigoIntegracaoErpRepresentante = analiseMercado.codigoIntegracaoErpRepresentante;
                    this.entity.cnpjCpfRepresentante = analiseMercado.cnpjCpfRepresentante;
                    this.entity.nomeRepresentante = analiseMercado.nomeRepresentante;
                    this.entity.nomeFantasiaRepresentante = analiseMercado.nomeFantasiaRepresentante;

                    if (!this.entity) { // se não retornar a análise de mercado do cliente no ano, criamos a análise
                        this.insert();
                    } else { // Se retornar a análise de mercado do cliente no ano, setamos os dados necessários para a análise anual caso não existirem
                        // Mês
                        if (!this.entity.mes) {
                            this.entity.mes = this.mes;
                        }
                        // Ano
                        if (!this.entity.ano) {
                            this.entity.ano = this.ano;
                        }
                    }

                    // Pesquisando as classificações
                    this.searchClassificacoes(idCliente, this.mes, this.ano);
                    this.isLoading = false;
                },
                (error: ApiError) => {
                    this.messageService.error(error.message, 'Erro ao carregar a análise de mercado do cliente', error);
                    this.isLoading = false;
                }
            );
    }

    /** Pesquisa as análiases mensais das classificações do cliente e ano */
    private searchClassificacoes(idCliente: number, mes: number, ano: number) {
        const filter = new ClienteAnaliseMercadoClassificacaoSearchLancamentosFilter();
        this.clienteAnaliseMercadoClassificacaoLancamentoHttpService.search(perspectivaProjetada.id, idCliente, periodicidadeMensal.id, false, mes, null, null, ano, filter)
            .subscribe(
                classificacoes => this.entity.classificacoes = classificacoes ? classificacoes : [],
                (error: ApiError) => {
                    this.messageService.error(error.message, 'Erro ao carregar as projeções dos segmentos do cliente', error);
                    this.entity.classificacoes = [];
                }
            );
    }

    /** Ao selecionar o cliente */
    onSelectCliente(cliente: Cliente) {
        if (cliente) {
            this.entity.idCliente = cliente.id;
            // Carregando representante do cliente
            this.findRepresentanteCliente(cliente.idRepresentante);
            // Pesquisando as classificações
            this.searchClassificacoes(this.entity.idCliente, this.mes, this.ano);
        } else {
            this.onClearCliente();
        }
    }

    /** Ao remover o cliente */
    onClearCliente() {
        this.entity.idCliente = null;
        this.entity.idRepresentante = null;
        this.entity.representante = null;
        this.entity.classificacoes = [];
    }

    /** Pesquisa o representante do cliente da baixa */
    private findRepresentanteCliente(idRepresentante: number) {
        this.representanteCrudHttpService.find(idRepresentante)
            .subscribe(
                representante => {
                    this.entity.representante = representante;
                    this.entity.idRepresentante = representante?.id;
                    this.entity.codigoIntegracaoErpRepresentante = representante?.pessoa?.codigoIntegracaoErp;
                    this.entity.nomeRepresentante = representante?.pessoa?.nome;
                    this.entity.nomeFantasiaRepresentante = representante?.pessoa?.nomeFantasia;
                },
                () => this.entity.representante = null
            );
    }

    /** Classificações */

    /** Verifica se pode editar a classificação */
    // canEditClassificacao(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
    //     // Somente pode editar a classificação se a sua projeção ainda não tiver sido salva no banco de dados, ou seja, quando ainda não possuir o ID na análise de mercado
    //     return !entityClassificacao?.id;
    // }

    /** Verifica se pode editar o lançamento por quantidade na classificação */
    canEditLancamentoQuantidade(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        // Se puder editar e o lançamento for por quantidade e a classificação não possuir linhas de produção
        return this.isLancamentoQuantidade(entityClassificacao);
    }

    /** Verifica se pode editar o lançamento por peso na classificação */
    canEditLancamentoPeso(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        // Se puder editar e o lançamento for por peso e a classificação não possuir linhas de produção
        return this.isLancamentoPeso(entityClassificacao);
    }

    /** Verifica se pode editar o lançamento por valor na classificação */
    canEditLancamentoValor(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        // Se puder editar e o lançamento for por valor e a classificação não possuir linhas de produção
        return this.isLancamentoValor(entityClassificacao);
    }

    /** Abre modal para inserir um novo lançamento de classificação na análise de mercado do cliente */
    createLancamentoClassificacao() {
        this.classificacaoCreateModal?.insert(perspectivaProjetada, this.entity?.idCliente, this.entity?.ano);
    }

    /** Após criar um lançamento de classificação na análise de mercado */
    onAfterCreateLancamentoClassificacao(entityClassificacao: ClienteAnaliseMercadoLancamento) {
        // this.entity.classificacoes?.push(entityClassificacao);
    }

    /** Exclui em memória a classificação da análise de mercado do cliente no ano */
    private delete(index: number) {
        if (index > -1) {
            this.entity.classificacoes.splice(index, 1);
        }
    }

    /** Remove as quantidades, peso e valores potenciais, pretendidos e projetados da análise na classificação informada */
    private clearAnaliseClassificacao(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento) {
        if (!entityClassificacao) {
            return;
        }

         // Quantidade projetada
         entityClassificacao.quantidadeProjetada = null;
         entityClassificacao.quantidadeProjetadaPerc = 0;
         entityClassificacao.quantidadeProjetadaValorUnitario = null;
         // Peso projetada
         entityClassificacao.pesoProjetado = null;
         entityClassificacao.pesoProjetadoPerc = 0;
         entityClassificacao.pesoProjetadoValorUnitario = null;
         // Valor projetado
         entityClassificacao.valorProjetado = null;
         entityClassificacao.valorProjetadoPerc = 0;
    }

    /** Verifica se o valor unitário é obrigatório na classificação quando o lançamento for por quantidade */
    isQuantidadeProjetadaValorUnitarioRequired(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        // Se o lançamento não for por quantidade então não é obrigatório
        if (!this.isLancamentoQuantidade(entityClassificacao)) {
            return false;
        }

        // Se possuir a quantidade projetada informada é obrigatório
        if (entityClassificacao.quantidadeProjetada !== null && entityClassificacao.quantidadeProjetada !== undefined) {
            return true;
        }

        return false;
    }

    /** Ao alterar o valor unitário na classificação quando o lançamento for por quantidade */
    onChangeQuantidadeProjetadaValorUnitario(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, quantidadeProjetadaValorUnitario: number) {
        entityClassificacao.quantidadeProjetadaValorUnitario = quantidadeProjetadaValorUnitario;

        // Calculando a quantidade e valor projetado da classificação
        this.onChangeQuantidadeProjetada(entityClassificacao, entityClassificacao.quantidadeProjetada);
    }

    /** Verifica se o valor unitário é obrigatório na classificação quando o lançamento for por peso */
    isPesoProjetadoValorUnitarioRequired(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento): boolean {
        // Se o lançamento não for por peso então não é obrigatório
        if (!this.isLancamentoPeso(entityClassificacao)) {
            return false;
        }

        // Se possuir o peso projetado informado é obrigatório
        if (entityClassificacao.pesoProjetado !== null && entityClassificacao.pesoProjetado !== undefined) {
            return true;
        }

        return false;
    }

    /** Ao alterar o valor unitário na classificação quando o lançamento for por peso */
    onChangePesoProjetadoValorUnitario(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, pesoProjetadoValorUnitario: number) {
        entityClassificacao.pesoProjetadoValorUnitario = pesoProjetadoValorUnitario;

        // Calculando o peso e valor projetado da classificação
        this.onChangePesoProjetado(entityClassificacao, entityClassificacao.pesoProjetado);
    }

    /** Ao alterar a quantidade projetada na classificação */
    onChangeQuantidadeProjetada(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, quantidadeProjetada: number) {
        this.changeQuantidadeProjetada(entityClassificacao, quantidadeProjetada);

        // Calculando o valor projetado
        const valorProjetado = calcularValorProjetado(entityClassificacao.quantidadeProjetada, entityClassificacao.quantidadeProjetadaValorUnitario, 2);
        this.onChangeValorProjetado(entityClassificacao, valorProjetado);
    }
    /** Altera a quantidade projetada na classificação */
    private changeQuantidadeProjetada(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, quantidadeProjetada: number) {
        entityClassificacao.quantidadeProjetada = quantidadeProjetada;
        // Calculando o percentual de participação da quantidade projetada sobre a quantidade potencial anual
        entityClassificacao.quantidadeProjetadaPerc = calcularQuantidadeProjetadaPerc(entityClassificacao.quantidadeProjetada, entityClassificacao.quantidadePotencial);
        // Recalculando o percentual da quantidade realizada
        entityClassificacao.quantidadeRealizadaPercProjetada = calcularQuantidadeRealizadaPerc(entityClassificacao.quantidadeRealizada, entityClassificacao.quantidadeProjetada);
        // Obtendo o status projetado
        entityClassificacao.statusProjetado = this.clienteAnaliseMercadoStatusProjetadoService.getStatusProjetado(entityClassificacao.statusProjetado, entityClassificacao.quantidadeProjetada, entityClassificacao.pesoProjetado, entityClassificacao.valorProjetado, entityClassificacao.quantidadeRealizada, entityClassificacao.pesoRealizado, entityClassificacao.valorRealizado);
    }

    /** Ao alterar o peso projetado na classificação */
    onChangePesoProjetado(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, pesoProjetado: number) {
        this.changePesoProjetado(entityClassificacao, pesoProjetado);

        // Calculando o valor projetado anual
        const valorProjetado = calcularValorProjetado(entityClassificacao.pesoProjetado, entityClassificacao.pesoProjetadoValorUnitario, 2);
        this.onChangeValorProjetado(entityClassificacao, valorProjetado);
    }
    /** Altera o peso projetado na classificação */
    private changePesoProjetado(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, pesoProjetado: number) {
        entityClassificacao.pesoProjetado = pesoProjetado;
        // Calculando o percentual de participação do peso projetado sobre o peso potencial anual
        entityClassificacao.pesoProjetadoPerc = calcularPesoProjetadoPerc(entityClassificacao.pesoProjetado, entityClassificacao.pesoPotencial);
        // Recalculando o percentual do peso realizado
        entityClassificacao.pesoRealizadoPercProjetado = calcularPesoRealizadoPerc(entityClassificacao.pesoRealizado, entityClassificacao.pesoProjetado);
        // Obtendo o status projetado
        entityClassificacao.statusProjetado = this.clienteAnaliseMercadoStatusProjetadoService.getStatusProjetado(entityClassificacao.statusProjetado, entityClassificacao.quantidadeProjetada, entityClassificacao.pesoProjetado, entityClassificacao.valorProjetado, entityClassificacao.quantidadeRealizada, entityClassificacao.pesoRealizado, entityClassificacao.valorRealizado);
    }

    /** Ao alterar o valor projetado na classificação */
    onChangeValorProjetado(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, valorProjetado: number) {
        this.changeValorProjetado(entityClassificacao, valorProjetado);
    }
    /** Altera o valor prjetado na classificação */
    private changeValorProjetado(entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento, valorProjetado: number) {
        entityClassificacao.valorProjetado = valorProjetado;
        // Calculando o percentual de participação do valor projetado sobre o valor potencial anual
        entityClassificacao.valorProjetadoPerc = calcularValorProjetadoPerc(entityClassificacao.valorProjetado, entityClassificacao.valorPotencial);
        // Recalculando o percentual do valor realizado
        entityClassificacao.valorRealizadoPercProjetado = calcularValorRealizadoPerc(entityClassificacao.valorRealizado, entityClassificacao.valorProjetado);
        // Obtendo o status projetado
        entityClassificacao.statusProjetado = this.clienteAnaliseMercadoStatusProjetadoService.getStatusProjetado(entityClassificacao.statusProjetado, entityClassificacao.quantidadeProjetada, entityClassificacao.pesoProjetado, entityClassificacao.valorProjetado, entityClassificacao.quantidadeRealizada, entityClassificacao.pesoRealizado, entityClassificacao.valorRealizado);
    }

    /** Remove a projeção de venda mensal do lançamento da classificação na análise de mercado */
    removeProjecaoMensal(entityClassificacaoIndex: number, entityClassificacao: ClienteAnaliseMercadoClassificacaoSearchLancamento) {
        if (!entityClassificacao) {
            return;
        }

        this.messageService.confirm(`Deseja realmente remover a projeção de venda mensal do segmento ${entityClassificacao.classificacoesNome}?`, 'Confirme').then((result) => {
            if (result) {

                // Se possuir o ID da análise da classificação, então removeos os valores projetados no banco de dados
                if (entityClassificacao.id) {
                    this.clienteAnaliseMercadoProjecaoClassificacaoHttpService.removeProjecaoMensal(entityClassificacao.id, this.mes)
                        .subscribe(
                            () => {
                                this.delete(entityClassificacaoIndex);
                                this.messageService.success('', `Projeção de venda mensal do segmento ${entityClassificacao.classificacoesNome} removido com sucesso`);
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, `Erro ao remover a projeção de venda mensal do segmento ${entityClassificacao.classificacoesNome}`);
                            }
                        );
                } else { // Se não, excluímos somente em memória
                    this.delete(entityClassificacaoIndex);
                    this.messageService.success('', `Projeção de venda mensal do segmento ${entityClassificacao.classificacoesNome} removido com sucesso`);
                }
            }
        });
    }

    /** Valida se todas as informações necessárias estão corretamente preenchidas para salvar as projeções */
    isValid(): boolean {
        // Validando as classificações adicionadas
        for (let i = 0; i < this.entity?.classificacoes?.length; i++) {
            const entityClassificacao = this.entity?.classificacoes[i];

            // Validando se o valor unitário não foi informado na classificação quando o lançamento for por quantidade
            if (this.isQuantidadeProjetadaValorUnitarioRequired(entityClassificacao) && (entityClassificacao.quantidadeProjetadaValorUnitario == null || entityClassificacao.quantidadeProjetadaValorUnitario === undefined)) {
                this.messageService.info(`Informe o preço médio no segmento ${entityClassificacao.classificacoesNome} ou remova a quantidade projetada`, 'É obrigatório informar o preço médio do segmento na projeção');
                return false;
            }

            // Validando se o valor unitário não foi informado na classificação quando o lançamento for por peso
            if (this.isPesoProjetadoValorUnitarioRequired(entityClassificacao) && (entityClassificacao.pesoProjetadoValorUnitario == null || entityClassificacao.pesoProjetadoValorUnitario === undefined)) {
                this.messageService.info(`Informe o preço médio no segmento ${entityClassificacao.classificacoesNome} ou remova o peso projetado`, 'É obrigatório informar o preço médio do segmento na projeção');
                return false;
            }
        }

        // Se o form não estiver válido
        if (!this.form.valid) {
            this.messageService.info('Para salvar é preciso informar corretamente todos os campos destacados em vermelho nas projeções', 'Informe corretamente todos os campos das projeções');
            return false;
        }

        return true;
    }

    /** Cria o objeto para salvar/atualizar as projeções mensais das classificações dos clientes */
    createEntitySave(): ClienteAnaliseMercadoProjecaoSave {
        if (!this.entity || !this.entity.classificacoes) {
            return;
        }

        // classificações
        const entitiesClassificacoesSave: ClienteAnaliseMercadoProjecaoClassificacaoSave[] = [];
        this.entity.classificacoes?.forEach(entityClassificacao => {
            const entityClassificacaoSave = new ClienteAnaliseMercadoProjecaoClassificacaoSave(entityClassificacao.id, entityClassificacao.idGrupoClassificacao, null, entityClassificacao.quantidadeProjetada, entityClassificacao.pesoProjetado, entityClassificacao.valorProjetado);
            entitiesClassificacoesSave.push(entityClassificacaoSave);
        });

        const entitySave = new ClienteAnaliseMercadoProjecaoSave(this.entity.id, this.entity.idCliente, this.mes, this.ano, entitiesClassificacoesSave);
        entitySave.searchProjecoesClientesAfterSave = true;
        return entitySave;
    }
}
